import { useState, useCallback } from 'react';
import { adminApi } from '../api/client';
import { useApi } from '../hooks/useApi';
import { Loading, ApiError, EmptyState } from '../components/ui/Loading';
import { Badge } from '../components/ui/Badge';
import { Modal } from '../components/ui/Modal';
import { Avatar } from '../components/ui/Avatar';
import { Pagination } from '../components/ui/Pagination';
import { useToast } from '../context/ToastContext';
import { formatDate, formatDateTime, timeAgo, paginate } from '../utils/helpers';

// ─── Workflow v3.2 — clés alignées sur les workflow_stage exacts du backend ───
const WORKFLOW_STEPS = [
  { key: 'submitted',         label: 'Demande soumise',        icon: '📝', action: null },
  { key: 'contract_sent',     label: 'Contrat envoyé',         icon: '📤', action: null },
  { key: 'contract_accepted', label: 'Contrat accepté',        icon: '📄', action: null }, // ← backend : contract_accepted (pas contract_signed)
  { key: 'phone_confirmed',   label: 'Confirmé par téléphone', icon: '📞', action: 'phone_confirm',    btnLabel: '📞 Confirmer appel téléphonique' },
  { key: 'meeting_scheduled', label: 'RDV programmé',          icon: '📅', action: 'schedule_meeting', btnLabel: '📅 Programmer le RDV' },
  { key: 'legalized',         label: 'Légalisé',               icon: '✅', action: 'legalize',          btnLabel: '✅ Marquer légalisé (documents signés)' },
  { key: 'approved',          label: 'Demande approuvée',      icon: '👍', action: 'approve_final',     btnLabel: '👍 Approuver définitivement' },
  { key: 'account_opened',    label: 'Compte activé',          icon: '🎉', action: 'open_account',      btnLabel: '🎉 Créer le compte & envoyer email' },
];

// ─── Upload contrat PDF (admin) ────────────────────────────────────────────────
const ContractUploadSection = () => {
  const toast = useToast();
  const [file,    setFile]    = useState(null);
  const [version, setVersion] = useState('v1.0');
  const [saving,  setSaving]  = useState(false);
  const { data: metaRaw, loading: metaLoading, refetch: metaRefetch } =
    useApi(() => adminApi.getContractMeta());
  const meta = metaRaw?.data;

  const handleUpload = async () => {
    if (!file) { toast.error('Sélectionnez un fichier PDF.'); return; }
    if (!version.trim()) { toast.error('La version est obligatoire.'); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('contract', file);
      fd.append('version', version.trim());
      await adminApi.uploadContract(fd);
      toast.success(`Contrat ${version} mis en ligne ✓`);
      setFile(null);
      metaRefetch();
    } catch (err) {
      toast.error(err.message || 'Erreur lors de l\'upload');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card" style={{ marginBottom: 20 }}>
      <div className="card-header">
        <div className="card-title">📄 Contrat PDF en vigueur</div>
        <div className="card-subtitle">Téléchargé par les candidats lors de l'inscription</div>
      </div>

      {metaLoading ? <Loading text="Vérification..." /> : meta?.available ? (
        <div className="inline-alert inline-alert-green" style={{ marginBottom: 16 }}>
          <span>✅</span>
          <div style={{ fontSize: 12 }}>
            <strong>Version {meta.version}</strong> en ligne depuis le {formatDate(meta.uploaded_at)}.
            Nom : <code>{meta.original_name}</code>.
            <a
              href={`${process.env.REACT_APP_API_URL || 'http://localhost:3000/api/v1'}/enterprise/contract/download`}
              target="_blank" rel="noopener noreferrer"
              className="btn btn-ghost btn-sm" style={{ marginLeft: 10 }}
            >
              ⬇ Télécharger
            </a>
          </div>
        </div>
      ) : (
        <div className="inline-alert inline-alert-yellow" style={{ marginBottom: 16 }}>
          <span>⚠️</span>
          <div style={{ fontSize: 12 }}>
            <strong>Aucun contrat PDF en ligne.</strong> Les candidats verront un message d'attente.
            Uploadez le contrat ci-dessous.
          </div>
        </div>
      )}

      <div className="form-row">
        <div className="form-group">
          <label className="form-label">Fichier PDF * (max 10 Mo)</label>
          <input type="file" accept=".pdf" className="form-input" style={{ padding: '6px 10px' }}
            onChange={e => setFile(e.target.files?.[0] || null)} />
          {file && <div className="form-hint">✓ {file.name} ({(file.size / 1024 / 1024).toFixed(2)} Mo)</div>}
        </div>
        <div className="form-group" style={{ maxWidth: 140 }}>
          <label className="form-label">Version *</label>
          <input type="text" className="form-input" placeholder="v1.0" value={version}
            onChange={e => setVersion(e.target.value)} />
        </div>
        <div className="form-group" style={{ display: 'flex' }}>
          <button className="btn btn-primary" style={{ alignSelf: 'flex-end' }}
            onClick={handleUpload} disabled={saving || !file}>
            {saving ? <span className="spinner" style={{ width: 14, height: 14 }} /> : '⬆ Mettre en ligne'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Pipeline admin v3.2 ──────────────────────────────────────────────────────
const STAGE_LABELS = {
  submitted:                   { label: 'Soumises',        color: 'var(--text3)'  },
  contract_sent:               { label: 'Contrat envoyé',  color: 'var(--text2)'  },
  contract_accepted:           { label: 'Contrat accepté', color: 'var(--blue)'   }, // ← aligné backend
  phone_confirmed:             { label: 'Tél. confirmé',   color: 'var(--orange)' },
  meeting_scheduled:           { label: 'RDV prog.',       color: 'var(--purple)' },
  legalized_awaiting_approval: { label: 'À approuver',     color: 'var(--accent)' },
  approved_awaiting_account:   { label: 'À activer',       color: 'var(--green)'  },
  account_opened:              { label: 'Actifs',           color: 'var(--green)'  },
  rejected:                    { label: 'Rejetées',        color: 'var(--red)'    },
};

const PipelineTab = () => {
  const toast = useToast();
  const [stageFilter,  setStageFilter]  = useState('');
  const [search,       setSearch]       = useState('');
  const [selected,     setSelected]     = useState(null);
  const [workflow,     setWorkflow]     = useState(null);
  const [loadingWF,    setLoadingWF]    = useState(false);
  const [actioning,    setActioning]    = useState(null);
  const [meetingModal, setMeetingModal] = useState(false);
  const [meetingDate,  setMeetingDate]  = useState('');
  const [meetingAgent, setMeetingAgent] = useState('');
  const [rejectModal,  setRejectModal]  = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [accountInfo,  setAccountInfo]  = useState(null);

  const { data: raw, loading, error, refetch } = useApi(
    () => adminApi.getEnterprisePipeline(stageFilter ? { workflow_stage: stageFilter } : {}),
    [stageFilter]
  );

  const counts   = raw?.data?.counts   || {};
  const requests = raw?.data?.requests || [];

  const filtered = requests.filter(r => {
    const q = search.toLowerCase();
    return !q
      || r.company_name?.toLowerCase().includes(q)
      || r.contact_name?.toLowerCase().includes(q)
      || r.contact_email?.toLowerCase().includes(q);
  });

  const openWorkflow = async (req) => {
    console.log('[openWorkflow] req pipeline:', JSON.stringify(req, null, 2));
    // Enrichir selected avec les champs du pipeline qui peuvent manquer
    setSelected({
      ...req,
      company_tax_id:       req.ice || req.company_tax_id || null,
      company_registration: req.rc_number || req.company_registration || null,
    });
    setWorkflow(null);
    setLoadingWF(true);
    try {
      const res = await adminApi.getEnterpriseWorkflow(req.request_id);
      const raw = res?.data;

      // Le backend retourne "workflow_stage" (pas "current_step") — on normalise ici
      setWorkflow({
        current_step:         raw.workflow_stage         || 'submitted',
        contract_accepted_at: raw.contract_accepted_at   || null,
        phone_confirmed_at:   raw.phone_confirmed_at     || null,
        meeting_date:         raw.meeting_date           || null,
        meeting_agent:        raw.meeting_agent_name     || (raw.meeting_agent_id ? `Agent #${raw.meeting_agent_id}` : null),
        meeting_agent_id:     raw.meeting_agent_id       || null,
        legalized_at:         raw.legalized_at           || null,
        notary_name:          raw.notary_name            || null,
        legalization_date:    raw.legalization_date      || raw.legalized_at || null,
        approved_at:          raw.approved_final_at      || raw.approved_at  || null,
        admin_notes:          raw.admin_notes            || null,
        account_opened_at:    raw.account_opened_at      || (raw.account_user_id ? raw.password_notified_at : null),
        account_user_id:      raw.account_user_id        || null,
        rejection_reason:     raw.rejection_reason       || null,
        // champs bruts utiles pour l'affichage
        cin:                  raw.cin,
        ice:                  raw.ice,
        rc_number:            raw.rc_number,
        status:               raw.status,
      });
    } catch (err) {
      // Fallback depuis les données du tableau pipeline
      setWorkflow({
        current_step:         req.workflow_stage         || 'submitted',
        phone_confirmed_at:   req.phone_confirmed_at     || null,
        meeting_date:         req.meeting_date           || null,
        legalized_at:         req.legalized_at           || null,
        contract_accepted_at: req.contract_accepted_at   || null,
        account_user_id:      req.account_user_id        || null,
      });
    } finally {
      setLoadingWF(false);
    }
  };

  const [legalModal,   setLegalModal]   = useState(false);
  const [legalDate,    setLegalDate]    = useState('');
  const [notaryName,   setNotaryName]   = useState('');
  const [approveModal, setApproveModal] = useState(false);
  const [adminNotes,   setAdminNotes]   = useState('');

  const doAction = async (action) => {
    if (!selected) return;
    if (action === 'schedule_meeting') { setMeetingModal(true); return; }
    if (action === 'legalize')         { setLegalDate(''); setNotaryName(''); setLegalModal(true); return; }
    if (action === 'approve_final')    { setAdminNotes(''); setApproveModal(true); return; }

    setActioning(action);
    try {
      let res;
      if (action === 'phone_confirm') res = await adminApi.phoneConfirmEnterprise(selected.request_id);
      if (action === 'open_account')  res = await adminApi.openAccountEnterprise(selected.request_id);

      // Mise à jour optimiste
      const STEP_MAP = { phone_confirm: 'phone_confirmed', open_account: 'account_opened' };
      const newStep  = STEP_MAP[action];
      if (newStep) {
        setWorkflow(prev => ({ ...(prev || {}), current_step: newStep, [`${newStep}_at`]: new Date().toISOString() }));
      }

      if (action === 'open_account') {
        if (res?.data?.email_sent === false && res?.data?.temporary_password) {
          setAccountInfo(res.data);
        } else {
          toast.success(`✓ Compte créé — email envoyé à ${selected.contact_email}`);
        }
      } else {
        toast.success('Étape validée ✓');
      }

      await _reloadWorkflow(selected?.request_id);
      refetch();
    } catch (err) {
      toast.error(`Erreur : ${err.message || 'Erreur lors de la validation'}`);
    } finally {
      setActioning(null);
    }
  };

  const doLegalize = async () => {
    setActioning('legalize');
    try {
      await adminApi.legalizeEnterprise(selected.request_id, {
        ...(legalDate  ? { legalization_date: legalDate }  : {}),
        ...(notaryName ? { notary_name: notaryName }       : {}),
      });
      setWorkflow(prev => ({
        ...prev, current_step: 'legalized',
        legalized_at: new Date().toISOString(),
        legalization_date: legalDate || null,
        notary_name: notaryName || null,
      }));
      toast.success('Légalisation enregistrée ✓');
      setLegalModal(false);
      await _reloadWorkflow(selected?.request_id); refetch();
    } catch (err) { toast.error(`Erreur : ${err.message}`); }
    finally { setActioning(null); }
  };

  const doApproveFinal = async () => {
    setActioning('approve_final');
    try {
      console.log('[approve_final] → request_id:', selected?.request_id, 'body:', { admin_notes: adminNotes || undefined });
      const res = await adminApi.approveFinalEnterprise(selected.request_id, {
        ...(adminNotes ? { admin_notes: adminNotes } : {}),
      });
      console.log('[approve_final] ← réponse backend:', JSON.stringify(res, null, 2));

      // Mise à jour optimiste immédiate
      setWorkflow(prev => {
        console.log('[approve_final] setWorkflow optimiste, prev.current_step:', prev?.current_step, '→ approved');
        return { ...prev, current_step: 'approved', approved_at: new Date().toISOString(), admin_notes: adminNotes || null };
      });

      toast.success('Demande approuvée définitivement ✓');
      setApproveModal(false);
      await _reloadWorkflow(selected?.request_id);
      refetch();
    } catch (err) {
      console.error('[approve_final] ERREUR:', err.message);
      toast.error(`Erreur : ${err.message}`);
    } finally {
      setActioning(null);
    }
  };

  const _reloadWorkflow = async (requestId) => {
    const id = requestId || selected?.request_id;
    console.log('[_reloadWorkflow] id:', id);
    if (!id) { console.warn('[_reloadWorkflow] pas de request_id — abandon'); return; }
    try {
      const wfRes = await adminApi.getEnterpriseWorkflow(id);
      console.log('[_reloadWorkflow] ← réponse raw:', JSON.stringify(wfRes?.data, null, 2));
      const raw = wfRes?.data;
      if (!raw?.workflow_stage) {
        console.warn('[_reloadWorkflow] workflow_stage absent dans la réponse');
        return;
      }
      setWorkflow(prev => {
        const next = {
          ...prev,
          current_step:         raw.workflow_stage,
          phone_confirmed_at:   raw.phone_confirmed_at                         || prev?.phone_confirmed_at,
          meeting_date:         raw.meeting_date                               || prev?.meeting_date,
          meeting_agent:        raw.meeting_agent_name || (raw.meeting_agent_id ? `Agent #${raw.meeting_agent_id}` : null) || prev?.meeting_agent,
          legalized_at:         raw.legalized_at                               || prev?.legalized_at,
          legalization_date:    raw.legalization_date                          || prev?.legalization_date,
          notary_name:          raw.notary_name                                || prev?.notary_name,
          approved_at:          raw.approved_final_at || raw.approved_at || raw.final_approved_at || prev?.approved_at,
          admin_notes:          raw.admin_notes                                || prev?.admin_notes,
          account_user_id:      raw.account_user_id                           || prev?.account_user_id,
          account_opened_at:    raw.account_opened_at                         || prev?.account_opened_at,
          ice:                  raw.ice                                        || prev?.ice,
          cin:                  raw.cin                                        || prev?.cin,
          rc_number:            raw.rc_number                                  || prev?.rc_number,
        };
        console.log('[_reloadWorkflow] setWorkflow:', prev?.current_step, '→', next.current_step);
        return next;
      });
    } catch (err) {
      console.error('[_reloadWorkflow] ERREUR:', err.message);
    }
  };
  const doScheduleMeeting = async () => {
    if (!meetingDate) { toast.error('La date est obligatoire.'); return; }
    setActioning('schedule_meeting');
    try {
      // agent_id = number si renseigné, sinon omis
      const agentIdNum = meetingAgent ? parseInt(meetingAgent, 10) : null;
      await adminApi.scheduleMeetingEnterprise(selected.request_id, {
        meeting_date: meetingDate,
        ...(agentIdNum && !isNaN(agentIdNum) ? { agent_id: agentIdNum } : {}),
      });

      setWorkflow(prev => ({
        ...(prev || {}),
        current_step:         'meeting_scheduled',
        meeting_scheduled_at:  new Date().toISOString(),
        meeting_date:          meetingDate,
        meeting_agent:         meetingAgent || null,
      }));

      toast.success('RDV programmé ✓');
      await _reloadWorkflow(selected?.request_id);
      refetch();
      setMeetingModal(false);
      setMeetingDate('');
      setMeetingAgent('');
    } catch (err) {
      toast.error(`Erreur : ${err.message || 'Erreur'}`);
    } finally {
      setActioning(null);
    }
  };

  const doReject = async () => {
    if (!rejectReason.trim()) { toast.error('La raison est obligatoire.'); return; }
    setActioning('reject');
    try {
      await adminApi.rejectEnterpriseWorkflow(
        (rejectModal || selected).request_id,
        { reason: rejectReason.trim() }
      );
      toast.success('Demande rejetée — notification envoyée');
      setRejectModal(null);
      setSelected(null);
      setWorkflow(null);
      setRejectReason('');
      refetch();
    } catch (err) {
      toast.error(err.message || 'Erreur');
    } finally {
      setActioning(null);
    }
  };

  // Index de l'étape actuelle (déjà complétée)
  const currentStepIndex = workflow
    ? WORKFLOW_STEPS.findIndex(s => s.key === (workflow.current_step || 'submitted'))
    : -1;

  // Prochaine étape ACTIONNABLE (skip les étapes sans action comme contract_sent/signed)
  const nextActionableIndex = (() => {
    for (let i = currentStepIndex + 1; i < WORKFLOW_STEPS.length; i++) {
      if (WORKFLOW_STEPS[i].action) return i;
    }
    return -1;
  })();

  return (
    <div>
      {/* Compteurs cliquables par étape */}
      {Object.keys(counts).length > 0 && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 20 }}>
          {Object.entries(STAGE_LABELS).map(([key, cfg]) => {
            const count = counts[key] ?? 0;
            if (count === 0 && key === 'rejected') return null;
            return (
              <button key={key}
                onClick={() => setStageFilter(stageFilter === key ? '' : key)}
                style={{
                  padding: '6px 12px', borderRadius: 20, cursor: 'pointer',
                  border: `2px solid ${stageFilter === key ? cfg.color : 'var(--border)'}`,
                  background: stageFilter === key ? `${cfg.color}18` : 'var(--bg3)',
                  display: 'flex', gap: 6, alignItems: 'center',
                }}>
                <span style={{ fontWeight: 800, fontSize: 16, color: cfg.color, fontFamily: 'Syne' }}>{count}</span>
                <span style={{ fontSize: 12, color: count > 0 ? cfg.color : 'var(--text3)', fontWeight: 600 }}>{cfg.label}</span>
              </button>
            );
          })}
          {stageFilter && (
            <button className="btn btn-ghost btn-sm" onClick={() => setStageFilter('')}>✕ Tout afficher</button>
          )}
        </div>
      )}

      {/* Barre de recherche */}
      <div className="filter-bar" style={{ marginBottom: 16 }}>
        <div className="search-box">
          <span className="search-icon">🔍</span>
          <input className="search-input" placeholder="Entreprise, contact, email..."
            value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <button className="btn btn-ghost btn-sm" onClick={refetch}>↺</button>
        <span style={{ fontSize: 12, color: 'var(--text3)' }}>{filtered.length} dossier(s)</span>
      </div>

      {/* Tableau pipeline */}
      <div className="card" style={{ padding: 0 }}>
        {loading ? <Loading text="Chargement du pipeline..." />
          : error ? <ApiError message={error} onRetry={refetch} />
          : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Entreprise</th>
                    <th>Contact</th>
                    <th>ICE / RC / CIN</th>
                    <th>Étape</th>
                    <th>Date</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(r => {
                    const stage = r.client_display_stage;
                    return (
                      <tr key={r.request_id}>
                        <td>
                          <div className="td-main">{r.company_name}</div>
                          <div className="td-sub">{r.business_type || '—'}</div>
                        </td>
                        <td>
                          <div className="td-main">{r.contact_name}</div>
                          <div className="td-sub">{r.contact_email}</div>
                          <div className="td-sub">📞 {r.contact_phone}</div>
                        </td>
                        <td style={{ fontSize: 12, color: 'var(--text2)' }}>
                          <div>ICE : {r.company_tax_id || '—'}</div>
                          <div>RC : {r.company_registration || '—'}</div>
                          <div>CIN : {r.cin || '—'}</div>
                        </td>
                        <td>
                          {stage ? (
                            <div>
                              <div style={{ fontWeight: 600, fontSize: 12 }}>
                                Étape {stage.step}/{stage.total}
                              </div>
                              <div style={{ fontSize: 11, color: 'var(--text2)' }}>{stage.label}</div>
                            </div>
                          ) : (
                            <div style={{ fontSize: 12, color: 'var(--text3)' }}>
                              {r.workflow_stage || '—'}
                            </div>
                          )}
                          {r.status === 'rejected' && (
                            <Badge className="badge-red" style={{ marginTop: 4 }}>Rejetée</Badge>
                          )}
                        </td>
                        <td style={{ fontSize: 12, color: 'var(--text2)', whiteSpace: 'nowrap' }}>
                          {formatDate(r.created_at)}
                        </td>
                        <td>
                          <div style={{ display: 'flex', gap: 5 }}>
                            <button className="btn btn-primary btn-sm" onClick={() => openWorkflow(r)}>
                              Gérer →
                            </button>
                            {r.status !== 'rejected' && r.account_user_id == null && (
                              <button className="btn btn-danger btn-sm"
                                onClick={() => { setRejectModal(r); setRejectReason(''); }}>
                                ✕
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {filtered.length === 0 && !loading && (
                <EmptyState icon="🏢" title="Aucun dossier"
                  text="Aucune demande d'inscription entreprise pour ces filtres." />
              )}
            </div>
          )}
      </div>

      {/* ── MODALE WORKFLOW ── */}
      <Modal
        open={!!selected}
        onClose={() => { setSelected(null); setWorkflow(null); }}
        title={`Pipeline — ${selected?.company_name}`}
        size="md"
        footer={
          <div style={{ display: 'flex', gap: 8, width: '100%' }}>
            {selected?.status !== 'rejected' && selected?.account_user_id == null && (
              <button className="btn btn-danger btn-sm"
                onClick={() => { setRejectModal(selected); setRejectReason(''); }}
                disabled={!!actioning}>
                ✕ Rejeter la demande
              </button>
            )}
            <button className="btn btn-secondary" style={{ marginLeft: 'auto' }}
              onClick={() => { setSelected(null); setWorkflow(null); }}>
              Fermer
            </button>
          </div>
        }
      >
        {selected && (
          <div>
            {/* Infos du dossier */}
            <div style={{ background: 'var(--bg3)', borderRadius: 10, padding: '12px 16px', marginBottom: 20 }}>
              {[
                { l: 'Contact',   v: `${selected.contact_name} — ${selected.contact_email}` },
                { l: 'Téléphone', v: selected.contact_phone },
                { l: 'ICE',       v: selected.ice || selected.company_tax_id || workflow?.ice || '—' },
                { l: 'RC',        v: selected.rc_number || selected.company_registration || workflow?.rc_number || '—' },
                { l: 'CIN',       v: selected.cin || workflow?.cin || '—' },
                { l: 'Adresse',   v: selected.address || '—' },
              ].map((r, i) => (
                <div key={i} className="info-row" style={{ padding: '4px 0' }}>
                  <span className="info-label">{r.l}</span>
                  <span className="info-value">{r.v}</span>
                </div>
              ))}
            </div>

            {/* Statut et prochaine action */}
            {loadingWF ? <Loading text="Chargement du workflow..." /> : (
              <>
                {/* Bannière état courant */}
                {workflow && (() => {
                  const current = WORKFLOW_STEPS[currentStepIndex];
                  const next    = WORKFLOW_STEPS[nextActionableIndex];
                  const done    = currentStepIndex >= WORKFLOW_STEPS.length - 1;
                  return (
                    <div className={`inline-alert ${done ? 'inline-alert-green' : 'inline-alert-blue'}`}
                      style={{ marginBottom: 16 }}>
                      <span>{done ? '🎉' : '📍'}</span>
                      <div style={{ fontSize: 12 }}>
                        {done ? (
                          <><strong>Compte activé</strong> — le client peut se connecter.</>
                        ) : (
                          <>
                            <strong>Étape actuelle :</strong> {current?.label || 'Demande soumise'}
                            {next && (
                              <> · <strong>Prochaine action :</strong> {next.btnLabel}</>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  );
                })()}

                {/* Timeline */}
                <div style={{ position: 'relative', paddingLeft: 24 }}>
                  <div style={{
                    position: 'absolute', left: 11, top: 8, bottom: 8,
                    width: 2, background: 'var(--border)',
                  }} />
                  {WORKFLOW_STEPS.map((step, i) => {
                    const isDone       = i <= currentStepIndex;
                    const isNextAction = i === nextActionableIndex;
                    // Griser les étapes futures non-actionnables
                    const isWaiting    = !isDone && !isNextAction && i > currentStepIndex;

                    return (
                      <div key={step.key} style={{ display: 'flex', gap: 14, marginBottom: 16, position: 'relative' }}>
                        {/* Bullet */}
                        <div style={{
                          width: 22, height: 22, borderRadius: '50%', flexShrink: 0,
                          background: isDone
                            ? 'var(--green)'
                            : isNextAction
                              ? 'var(--accent)'
                              : 'var(--border)',
                          border: `2px solid ${isDone ? 'var(--green)' : isNextAction ? 'var(--accent)' : 'var(--border2)'}`,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: 11,
                          color: isDone || isNextAction ? '#fff' : 'var(--text3)',
                          zIndex: 1,
                        }}>
                          {isDone ? '✓' : step.icon}
                        </div>

                        <div style={{ flex: 1, opacity: isWaiting ? 0.45 : 1 }}>
                          <div style={{
                            fontSize: 13,
                            fontWeight: isDone || isNextAction ? 600 : 400,
                            color: isDone
                              ? 'var(--green)'
                              : isNextAction
                                ? 'var(--accent)'
                                : 'var(--text3)',
                          }}>
                            {step.label}
                          </div>

                          {/* Horodatage si disponible */}
                          {workflow?.[`${step.key}_at`] && (
                            <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>
                              {formatDateTime(workflow[`${step.key}_at`])}
                            </div>
                          )}

                          {/* Détail RDV */}
                          {workflow?.meeting_date && step.key === 'meeting_scheduled' && (isDone || isNextAction) && (
                            <div style={{ fontSize: 11, color: 'var(--text2)', marginTop: 2 }}>
                              📅 {formatDateTime(workflow.meeting_date)}
                              {workflow.meeting_agent && ` — ${workflow.meeting_agent}`}
                            </div>
                          )}

                          {/* Détail légalisation */}
                          {step.key === 'legalized' && isDone && (workflow?.legalization_date || workflow?.notary_name) && (
                            <div style={{ fontSize: 11, color: 'var(--text2)', marginTop: 2 }}>
                              {workflow.legalization_date && `📅 ${formatDate(workflow.legalization_date)}`}
                              {workflow.notary_name && ` — ${workflow.notary_name}`}
                            </div>
                          )}

                          {/* Notes admin */}
                          {step.key === 'approved' && isDone && workflow?.admin_notes && (
                            <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2, fontStyle: 'italic' }}>
                              "{workflow.admin_notes}"
                            </div>
                          )}

                          {/* Bouton action — uniquement sur la prochaine étape actionnable */}
                          {isNextAction && step.action && (
                            <button
                              className="btn btn-primary"
                              style={{ marginTop: 10 }}
                              onClick={() => doAction(step.action)}
                              disabled={!!actioning}
                            >
                              {actioning === step.action
                                ? <><span className="spinner" style={{ width: 14, height: 14 }} /> En cours...</>
                                : step.btnLabel}
                            </button>
                          )}

                          {/* Confirmation compte activé */}
                          {isDone && step.key === 'account_opened' && (
                            <div style={{ fontSize: 11, color: 'var(--green)', marginTop: 4, fontWeight: 600 }}>
                              ✓ Compte actif — le client peut se connecter
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        )}
      </Modal>

      {/* ── MODALE LÉGALISATION ── */}
      <Modal open={legalModal} onClose={() => setLegalModal(false)}
        title="Enregistrer la légalisation" size="sm"
        footer={<>
          <button className="btn btn-secondary" onClick={() => setLegalModal(false)}>Annuler</button>
          <button className="btn btn-primary" onClick={doLegalize} disabled={actioning === 'legalize'}>
            {actioning === 'legalize'
              ? <><span className="spinner" style={{ width: 14, height: 14 }} /> En cours...</>
              : '✅ Confirmer la légalisation'}
          </button>
        </>}
      >
        <div className="inline-alert inline-alert-blue" style={{ marginBottom: 16 }}>
          <span>ℹ️</span>
          <div style={{ fontSize: 12 }}>
            Confirmez que les documents ont été légalisés. La date et le notaire sont optionnels.
          </div>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Date de légalisation</label>
            <input type="date" className="form-input" value={legalDate}
              onChange={e => setLegalDate(e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Notaire / Autorité</label>
            <input type="text" className="form-input" placeholder="Me. Dupont" value={notaryName}
              onChange={e => setNotaryName(e.target.value)} />
          </div>
        </div>
      </Modal>

      {/* ── MODALE APPROBATION FINALE ── */}
      <Modal open={approveModal} onClose={() => setApproveModal(false)}
        title="Approuver définitivement la demande" size="sm"
        footer={<>
          <button className="btn btn-secondary" onClick={() => setApproveModal(false)}>Annuler</button>
          <button className="btn btn-success" onClick={doApproveFinal} disabled={actioning === 'approve_final'}>
            {actioning === 'approve_final'
              ? <><span className="spinner" style={{ width: 14, height: 14 }} /> En cours...</>
              : '👍 Approuver définitivement'}
          </button>
        </>}
      >
        <div className="inline-alert inline-alert-green" style={{ marginBottom: 16 }}>
          <span>👍</span>
          <div style={{ fontSize: 12 }}>
            Cette approbation débloque l'étape finale : <strong>création du compte</strong>.
            Vous pourrez ensuite cliquer sur "Créer le compte & envoyer email".
          </div>
        </div>
        <div className="form-group">
          <label className="form-label">Notes internes (optionnel)</label>
          <textarea className="form-textarea" rows={3}
            placeholder="Notes visibles uniquement par l'admin..."
            value={adminNotes} onChange={e => setAdminNotes(e.target.value)} />
        </div>
      </Modal>

      {/* ── MODALE RDV ── */}
      <Modal open={meetingModal} onClose={() => setMeetingModal(false)}
        title="Programmer un RDV physique" size="sm"
        footer={<>
          <button className="btn btn-secondary" onClick={() => setMeetingModal(false)}>Annuler</button>
          <button className="btn btn-primary" onClick={doScheduleMeeting} disabled={!!actioning}>
            {actioning === 'schedule_meeting'
              ? <><span className="spinner" style={{ width: 14, height: 14 }} /> En cours...</>
              : '📅 Confirmer le RDV'}
          </button>
        </>}
      >
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Date et heure *</label>
            <input type="datetime-local" className="form-input" value={meetingDate}
              onChange={e => setMeetingDate(e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Agent LAVEX (optionnel)</label>
            <input type="text" className="form-input" placeholder="Nom de l'agent responsable"
              value={meetingAgent} onChange={e => setMeetingAgent(e.target.value)} />
          </div>
        </div>
      </Modal>

      {/* ── MODALE REJET ── */}
      <Modal open={!!rejectModal} onClose={() => { setRejectModal(null); setRejectReason(''); }}
        title={`Rejeter — ${(rejectModal || selected)?.company_name}`} size="sm"
        footer={<>
          <button className="btn btn-secondary"
            onClick={() => { setRejectModal(null); setRejectReason(''); }}>
            Annuler
          </button>
          <button className="btn btn-danger" onClick={doReject}
            disabled={actioning === 'reject' || !rejectReason.trim()}>
            {actioning === 'reject'
              ? <><span className="spinner" style={{ width: 14, height: 14 }} /> En cours...</>
              : 'Rejeter définitivement'}
          </button>
        </>}
      >
        <div className="form-group">
          <label className="form-label">Raison du rejet *</label>
          <textarea className="form-textarea" rows={4}
            placeholder="Ex: Documents incomplets, secteur non éligible, informations incorrectes..."
            value={rejectReason} onChange={e => setRejectReason(e.target.value)} />
          <div className="form-hint">
            Le candidat recevra cette raison et pourra contacter le support.
          </div>
        </div>
      </Modal>

      {/* ── MODALE MOT DE PASSE TEMPORAIRE (si email échoué) ── */}
      <Modal open={!!accountInfo} onClose={() => setAccountInfo(null)}
        title="⚠️ Compte créé — Email non envoyé" size="sm"
        footer={
          <button className="btn btn-primary" style={{ width: '100%' }}
            onClick={() => setAccountInfo(null)}>
            Compris — j'ai noté le mot de passe
          </button>
        }
      >
        {accountInfo && (
          <div>
            <div className="inline-alert inline-alert-yellow" style={{ marginBottom: 16 }}>
              <span>⚠️</span>
              <div style={{ fontSize: 12 }}>
                Le compte a été créé mais l'envoi d'email a échoué (SMTP).
                Communiquez <strong>manuellement</strong> ce mot de passe au client par téléphone ou autre canal sécurisé.
              </div>
            </div>
            <div style={{ background: 'var(--bg3)', borderRadius: 10, padding: '16px' }}>
              <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.8 }}>
                🔑 Mot de passe temporaire — à communiquer manuellement
              </div>
              <div style={{
                fontFamily: 'monospace', fontSize: 26, fontWeight: 800, letterSpacing: 4,
                color: 'var(--accent)', userSelect: 'all', textAlign: 'center', padding: '10px 0',
                background: 'var(--bg2)', borderRadius: 8, marginBottom: 8,
              }}>
                {accountInfo.temporary_password}
              </div>
              <div style={{ fontSize: 11, color: 'var(--text3)', textAlign: 'center' }}>
                Email : <strong>{accountInfo.email}</strong>
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

// ─── Comptes en attente (inscription app mobile) ───────────────────────────────
const PendingAccountsTab = () => {
  const toast = useToast();
  const { data: raw, loading, error, refetch, setData } = useApi(() => adminApi.getPendingValidations());
  const [selected,    setSelected]    = useState(null);
  const [rejectModal, setRejectModal] = useState(null);
  const [reason,      setReason]      = useState('');
  const [actioning,   setActioning]   = useState(false);
  const items = raw?.data || [];

  const removeItem = useCallback((userId) => {
    setData(prev => ({ ...prev, data: prev.data.filter(i => i.user_id !== userId) }));
  }, [setData]);

  const handleAction = async (userId, action, rejection_reason = null) => {
    setActioning(true);
    try {
      await adminApi.validateUser(userId, { action, ...(rejection_reason ? { rejection_reason } : {}) });
      removeItem(userId);
      toast.success(action === 'approve'
        ? '✓ Compte approuvé — Email avec accès envoyé automatiquement'
        : 'Compte rejeté — Email de notification envoyé');
      setSelected(null); setRejectModal(null); setReason('');
    } catch (err) { toast.error(err.message || 'Erreur'); }
    finally { setActioning(false); }
  };

  if (loading) return <Loading text="Chargement des validations..." />;
  if (error)   return <ApiError message={error} onRetry={refetch} />;

  return (
    <div>
      {items.length > 0 && (
        <div className="inline-alert inline-alert-yellow" style={{ marginBottom: 20 }}>
          <span>📋</span>
          <div>
            <strong>{items.length} compte(s) entreprise</strong> en attente d'activation —
            Ces entreprises ont créé un compte via l'application mobile.
          </div>
        </div>
      )}
      {items.length === 0 ? (
        <div className="card">
          <EmptyState icon="✅" title="Tout est traité"
            text="Aucun compte entreprise en attente d'activation." />
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {items.map(item => (
            <div key={item.user_id} className="card" style={{ borderLeft: '3px solid var(--orange)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
                  <Avatar name={item.name} size="lg" />
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 16, fontFamily: 'Syne', marginBottom: 3 }}>{item.name}</div>
                    <div style={{ fontSize: 12, color: 'var(--text2)' }}>{item.email}</div>
                    <div style={{ fontSize: 12, color: 'var(--text3)' }}>
                      {item.phone} · {formatDate(item.created_at)} ({timeAgo(item.created_at)})
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="btn btn-ghost btn-sm" onClick={() => setSelected(item)}>Détails</button>
                  <button className="btn btn-danger btn-sm"
                    onClick={() => { setRejectModal(item); setReason(''); }}
                    disabled={actioning}>✕ Rejeter</button>
                  <button className="btn btn-success btn-sm"
                    onClick={() => handleAction(item.user_id, 'approve')}
                    disabled={actioning}>
                    {actioning ? <span className="spinner" style={{ width: 12, height: 12 }} /> : '✓ Approuver'}
                  </button>
                </div>
              </div>
              <div className="divider" />
              <div className="grid-3" style={{ gap: 10 }}>
                {[
                  { label: '🏢 Entreprise', val: item.company_name },
                  { label: '📄 Registre',   val: item.company_registration },
                  { label: '🆔 ICE',        val: item.company_tax_id || <span style={{ color: 'var(--text3)' }}>Non fourni</span> },
                ].map((f, i) => (
                  <div key={i} style={{ background: 'var(--bg3)', borderRadius: 8, padding: '10px 14px' }}>
                    <div style={{ fontSize: 10, color: 'var(--text3)', marginBottom: 4 }}>{f.label}</div>
                    <div style={{ fontSize: 13, fontWeight: 500 }}>{f.val}</div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal open={!!selected} onClose={() => setSelected(null)} title="Compte en attente" size="md"
        footer={<>
          <button className="btn btn-secondary" onClick={() => setSelected(null)}>Fermer</button>
          <button className="btn btn-danger" onClick={() => { setRejectModal(selected); setSelected(null); }}>✕ Rejeter</button>
          <button className="btn btn-success" onClick={() => handleAction(selected?.user_id, 'approve')} disabled={actioning}>
            ✓ Approuver
          </button>
        </>}>
        {selected && (
          <div>
            <div className="detail-header">
              <Avatar name={selected.name} size="lg" />
              <div>
                <div className="detail-name">{selected.name}</div>
                <div className="detail-meta">{selected.email} · {selected.phone}</div>
              </div>
            </div>
            {[
              { l: 'Entreprise',  v: selected.company_name },
              { l: 'Registre',    v: selected.company_registration },
              { l: 'ICE',         v: selected.company_tax_id || '—' },
              { l: 'Catégorie',   v: 'A — Entreprise' },
              { l: 'Demande le',  v: formatDateTime(selected.created_at) },
            ].map((r, i) => (
              <div key={i} className="info-row">
                <span className="info-label">{r.l}</span>
                <span className="info-value">{r.v}</span>
              </div>
            ))}
          </div>
        )}
      </Modal>

      <Modal open={!!rejectModal} onClose={() => setRejectModal(null)} title="Rejeter le compte" size="sm"
        footer={<>
          <button className="btn btn-secondary" onClick={() => setRejectModal(null)}>Annuler</button>
          <button className="btn btn-danger"
            onClick={() => handleAction(rejectModal?.user_id, 'reject', reason)}
            disabled={actioning || !reason.trim()}>
            {actioning ? <span className="spinner" style={{ width: 14, height: 14 }} /> : 'Confirmer le rejet'}
          </button>
        </>}>
        {rejectModal && (
          <>
            <p style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 16 }}>
              Rejeter <strong>{rejectModal.name}</strong> ({rejectModal.company_name}).
              Un email de notification sera envoyé.
            </p>
            <div className="form-group">
              <label className="form-label">Raison du rejet *</label>
              <textarea className="form-textarea"
                placeholder="Ex: Documents incomplets..."
                value={reason} onChange={e => setReason(e.target.value)} />
            </div>
          </>
        )}
      </Modal>
    </div>
  );
};

// ─── Demandes entreprise v2.2 (formulaire public web, ancien workflow) ─────────
const EnterpriseRequestsTab = () => {
  const toast = useToast();
  const [statusFilter, setStatusFilter] = useState('pending');
  const [search,       setSearch]       = useState('');
  const [page,         setPage]         = useState(1);
  const [selected,     setSelected]     = useState(null);
  const [rejectModal,  setRejectModal]  = useState(null);
  const [reason,       setReason]       = useState('');
  const [language,     setLanguage]     = useState('fr');
  const [actioning,    setActioning]    = useState(false);

  const { data: raw, loading, error, refetch } = useApi(
    () => adminApi.getEnterpriseRequests({ status: statusFilter }),
    [statusFilter]
  );

  const requests = raw?.data || [];
  const filtered = requests.filter(r => {
    const q = search.toLowerCase();
    return !q
      || r.company_name?.toLowerCase().includes(q)
      || r.contact_name?.toLowerCase().includes(q)
      || r.contact_email?.toLowerCase().includes(q);
  });
  const paged = paginate(filtered, page, 10);

  const STATUS_CFG = {
    pending:  { label: 'En attente', cls: 'badge-yellow' },
    approved: { label: 'Approuvée',  cls: 'badge-green'  },
    rejected: { label: 'Rejetée',    cls: 'badge-red'    },
  };

  const handleApprove = async () => {
    setActioning(true);
    try {
      await adminApi.approveEnterpriseRequest(selected.request_id, { language });
      toast.success(`✓ Compte créé — Email + mot de passe temporaire envoyé à ${selected.contact_email}`);
      setSelected(null); refetch();
    } catch (err) { toast.error(err.message || 'Erreur'); }
    finally { setActioning(false); }
  };

  const handleReject = async () => {
    setActioning(true);
    try {
      await adminApi.rejectEnterpriseRequest(rejectModal.request_id, { rejection_reason: reason });
      toast.success('Demande rejetée — Email de notification envoyé');
      setRejectModal(null); setReason(''); refetch();
    } catch (err) { toast.error(err.message || 'Erreur'); }
    finally { setActioning(false); }
  };

  return (
    <div>
      <div className="inline-alert inline-alert-blue" style={{ marginBottom: 20 }}>
        <span>ℹ️</span>
        <div style={{ fontSize: 12 }}>
          <strong>Workflow v2.2 :</strong> Formulaire public sans compte.
          L'admin crée le compte manuellement — le système génère un mot de passe temporaire et envoie l'email automatiquement.
          Pour les nouvelles inscriptions, utilisez l'onglet <strong>Pipeline entreprises</strong>.
        </div>
      </div>

      <div className="filter-bar">
        <div className="search-box">
          <span className="search-icon">🔍</span>
          <input className="search-input" placeholder="Entreprise, contact, email..."
            value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} />
        </div>
        <select className="filter-select" value={statusFilter}
          onChange={e => { setStatusFilter(e.target.value); setPage(1); }}>
          <option value="pending">En attente</option>
          <option value="approved">Approuvées</option>
          <option value="rejected">Rejetées</option>
        </select>
        <span style={{ fontSize: 12, color: 'var(--text3)', whiteSpace: 'nowrap' }}>{filtered.length} demandes</span>
        <button className="btn btn-ghost btn-sm" onClick={refetch}>↺</button>
      </div>

      <div className="card" style={{ padding: 0 }}>
        {loading ? <Loading text="Chargement..." />
          : error ? <ApiError message={error} onRetry={refetch} />
          : (
            <>
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr><th>Entreprise</th><th>Contact</th><th>Secteur</th><th>Statut</th><th>Date</th><th>Actions</th></tr>
                  </thead>
                  <tbody>
                    {paged.items.map(r => {
                      const sc = STATUS_CFG[r.status] || { label: r.status, cls: 'badge-gray' };
                      return (
                        <tr key={r.request_id}>
                          <td>
                            <div className="td-main">{r.company_name}</div>
                            <div className="td-sub">{r.company_registration || '—'}</div>
                          </td>
                          <td>
                            <div className="td-main">{r.contact_name}</div>
                            <div className="td-sub">{r.contact_email}</div>
                            <div className="td-sub">{r.contact_phone}</div>
                          </td>
                          <td style={{ fontSize: 12, color: 'var(--text2)' }}>{r.business_type || '—'}</td>
                          <td><Badge className={sc.cls}>{sc.label}</Badge></td>
                          <td style={{ fontSize: 12, color: 'var(--text2)', whiteSpace: 'nowrap' }}>
                            {formatDate(r.created_at)}
                            <div style={{ color: 'var(--text3)' }}>{timeAgo(r.created_at)}</div>
                          </td>
                          <td>
                            <div style={{ display: 'flex', gap: 6 }}>
                              <button className="btn btn-ghost btn-sm" onClick={() => setSelected(r)}>Voir</button>
                              {r.status === 'pending' && (
                                <button className="btn btn-danger btn-sm"
                                  onClick={() => { setRejectModal(r); setReason(''); }}>✕</button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {paged.items.length === 0 && !loading && (
                  <EmptyState icon="🏢" title="Aucune demande"
                    text="Aucune demande entreprise dans cette catégorie." />
                )}
              </div>
              {paged.total > 0 && (
                <div style={{ padding: '0 16px 16px' }}>
                  <Pagination {...paged} onPage={setPage} />
                </div>
              )}
            </>
          )}
      </div>

      {/* Détail + Approbation */}
      <Modal open={!!selected} onClose={() => setSelected(null)}
        title={selected?.status === 'pending'
          ? `Créer le compte — ${selected?.company_name}`
          : `Demande — ${selected?.company_name}`}
        size="md"
        footer={selected?.status === 'pending' ? (
          <>
            <button className="btn btn-secondary" onClick={() => setSelected(null)}>Annuler</button>
            <button className="btn btn-danger"
              onClick={() => { setRejectModal(selected); setSelected(null); }}>✕ Rejeter</button>
            <button className="btn btn-success" onClick={handleApprove} disabled={actioning}>
              {actioning
                ? <span className="spinner" style={{ width: 14, height: 14 }} />
                : '✓ Créer le compte & Envoyer email'}
            </button>
          </>
        ) : (
          <button className="btn btn-secondary" onClick={() => setSelected(null)}>Fermer</button>
        )}
      >
        {selected && (
          <div>
            <div style={{ background: 'var(--bg3)', borderRadius: 10, padding: '14px 16px', marginBottom: 16 }}>
              <div style={{ fontSize: 16, fontWeight: 700, fontFamily: 'Syne', marginBottom: 4 }}>
                🏢 {selected.company_name}
              </div>
              {selected.description && (
                <div style={{ fontSize: 13, color: 'var(--text2)', lineHeight: 1.5 }}>{selected.description}</div>
              )}
            </div>
            {[
              { l: 'Contact',    v: selected.contact_name },
              { l: 'Email',      v: selected.contact_email },
              { l: 'Téléphone',  v: selected.contact_phone },
              { l: 'Registre',   v: selected.company_registration || '—' },
              { l: 'ICE',        v: selected.company_tax_id || '—' },
              { l: 'Secteur',    v: selected.business_type || '—' },
              { l: 'Adresse',    v: selected.address || '—' },
              { l: 'Demande le', v: formatDateTime(selected.created_at) },
            ].map((r, i) => (
              <div key={i} className="info-row">
                <span className="info-label">{r.l}</span>
                <span className="info-value">{r.v}</span>
              </div>
            ))}

            {selected.status === 'pending' && (
              <>
                <div className="divider" />
                <div className="inline-alert inline-alert-green" style={{ marginBottom: 16 }}>
                  <span>📧</span>
                  <div style={{ fontSize: 12 }}>
                    Un compte <strong>Catégorie A</strong> sera créé avec un mot de passe temporaire.
                    Email envoyé à <strong>{selected.contact_email}</strong>.
                    Le client devra le changer à la première connexion.
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Langue de l'email</label>
                  <select className="form-select" value={language} onChange={e => setLanguage(e.target.value)}>
                    <option value="fr">Français</option>
                    <option value="ar">Arabe</option>
                    <option value="en">Anglais</option>
                  </select>
                </div>
              </>
            )}

            {selected.status === 'rejected' && selected.rejection_reason && (
              <div className="inline-alert inline-alert-red" style={{ marginTop: 12 }}>
                <span>❌</span>
                <div><strong>Raison du rejet :</strong> {selected.rejection_reason}</div>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* Rejet */}
      <Modal open={!!rejectModal} onClose={() => setRejectModal(null)} title="Rejeter la demande" size="sm"
        footer={<>
          <button className="btn btn-secondary" onClick={() => setRejectModal(null)}>Annuler</button>
          <button className="btn btn-danger" onClick={handleReject}
            disabled={actioning || !reason.trim()}>
            {actioning ? <span className="spinner" style={{ width: 14, height: 14 }} /> : 'Rejeter'}
          </button>
        </>}>
        {rejectModal && (
          <>
            <p style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 16 }}>
              Rejeter la demande de <strong>{rejectModal.company_name}</strong>.
              Le demandeur sera notifié par email.
            </p>
            <div className="form-group">
              <label className="form-label">Raison du rejet *</label>
              <textarea className="form-textarea"
                placeholder="Ex: Secteur non éligible, informations insuffisantes..."
                value={reason} onChange={e => setReason(e.target.value)} />
            </div>
          </>
        )}
      </Modal>
    </div>
  );
};

// ─── Page principale ──────────────────────────────────────────────────────────
export const Validations = () => {
  const [tab, setTab] = useState('pipeline');
  return (
    <div>
      <div style={{
        display: 'flex', gap: 4, marginBottom: 24,
        background: 'var(--bg2)', border: '1px solid var(--border)',
        borderRadius: 12, padding: 4, width: 'fit-content', flexWrap: 'wrap',
      }}>
        {[
          { key: 'pipeline',            label: '🔄 Pipeline entreprises', sub: '6 étapes v3.2' },
          { key: 'enterprise_requests', label: '🏢 Demandes anciennes',   sub: 'Formulaire v2.2' },
          { key: 'contract',            label: '📄 Contrat PDF',           sub: 'Upload & gestion' },
          { key: 'pending_accounts',    label: '✅ Comptes en attente',    sub: 'App mobile' },
        ].map(t => (
          <button key={t.key}
            className={`btn btn-sm ${tab === t.key ? 'btn-primary' : 'btn-ghost'}`}
            onClick={() => setTab(t.key)}
            style={{ borderRadius: 9, flexDirection: 'column', height: 'auto', padding: '8px 16px', gap: 2 }}>
            <span>{t.label}</span>
            <span style={{ fontSize: 10, opacity: 0.65, fontWeight: 400 }}>{t.sub}</span>
          </button>
        ))}
      </div>
      {tab === 'pipeline'            && <PipelineTab />}
      {tab === 'enterprise_requests' && <EnterpriseRequestsTab />}
      {tab === 'contract'            && <ContractUploadSection />}
      {tab === 'pending_accounts'    && <PendingAccountsTab />}
    </div>
  );
};